import { AxTabbedBase } from '@theme/ax-tabbed-base';
import { requestNostoReco, fetchNostoCards, attributeRecoClicks } from '@theme/nosto-reco-core';

/**
 * Tabbed product lists — Swiper-based.
 *
 * Rend des cartes produit dans des carousels par onglet. Toute la mécanique
 * (tabs, swiper, Section Rendering API) vit dans AxTabbedBase.
 *
 * ── Onglet piloté par Nosto ──────────────────────────────────────────────────
 * Un onglet dont le block a un `nosto_placement` (côté Liquid : le carousel porte
 * `data-nosto-placement`, le fallback rend des placeholders, l'onglet est forcé en
 * carousel) reçoit ses produits des recommandations Nosto au lieu d'une collection.
 *
 * On surcharge le hook `prepareCarousel` de la base : AVANT l'init Swiper d'un
 * carousel Nosto, on résout les cartes via `@theme/nosto-reco-core` (même flux que la
 * reco PDP et le sidecart) et on remplit le `[data-ax-fallback]`. Swiper s'initialise
 * ensuite directement avec les vraies cartes → pas de flash skeleton→cartes.
 *
 * Les placements des onglets non-actifs (dont le carousel n'existe pas encore tant que
 * la Section Rendering API n'a pas chargé les panneaux) sont pré-chauffés à l'idle :
 * le 1er clic peuple alors depuis le cache (instantané).
 *
 * Chaque slide porte `data-nosto-product-id` pour l'attribution gérée par
 * `nosto-cart-sync.js` (le carousel porte déjà `data-nosto-placement`).
 *
 * @extends {AxTabbedBase}
 */
class AxTabbedProductListsComponent extends AxTabbedBase {
  static bemPrefix = 'ax-tabbed-product-lists';
  static SECTION_NAME = 'card-product-nosto';

  /** @type {Map<string, Promise<Array<{ product: Record<string, any>, cardEl: Element }>>>} */
  #nostoCache = new Map();

  async connectedCallback() {
    await super.connectedCallback();
    this.#prewarmNostoTabs();
  }

  /**
   * Hook base : peuple un carousel Nosto avant l'init Swiper. No-op pour un onglet
   * collection classique (pas de `data-nosto-placement`).
   * @param {HTMLElement} carousel
   */
  async prepareCarousel(carousel) {
    const placement = carousel?.dataset?.nostoPlacement;
    if (!placement) return;

    const fallback = carousel.querySelector('[data-ax-fallback]');
    if (!fallback) return;

    const max = parseInt(carousel.dataset.nostoProductsToShow ?? '', 10) || 8;
    const cards = await this.#getNostoCards(placement, max);

    if (cards.length === 0) {
      // Nosto n'a rien servi (visiteur sans historique / placement vide / lent).
      // → on remplace le skeleton par le repli collection, cloné depuis le <template>
      //   NON peint (donc jamais de flicker). Sinon (pas de collection) → on masque l'onglet.
      const template = carousel.querySelector('template[data-nosto-collection-fallback]');
      if (template instanceof HTMLTemplateElement && template.content.querySelector('.ax-tabbed-product-lists__slide')) {
        fallback.replaceChildren(template.content.cloneNode(true));
        document.dispatchEvent(new CustomEvent('reactivateLazyObserver'));
      } else {
        fallback.replaceChildren();
        this.#hideCarouselTab(carousel);
      }
      return;
    }

    // Réutilise la classe de slide du thème (mirror du fallback SSR).
    const slideClass =
      fallback.firstElementChild?.className || `${this.constructor.bemPrefix}__slide resource-list__item`;
    const fragment = document.createDocumentFragment();

    for (const { product, cardEl } of cards) {
      const slide = document.createElement('div');
      slide.className = slideClass;
      slide.dataset.nostoProductId = product.product_id;
      slide.appendChild(cardEl);
      fragment.appendChild(slide);
    }

    fallback.replaceChildren(fragment);
    document.dispatchEvent(new CustomEvent('reactivateLazyObserver'));

    // Attribution Nosto des clics « carte → fiche produit » (après injection des cartes).
    attributeRecoClicks(carousel, placement);
  }

  /**
   * Cartes Nosto pour un placement, mémoïsées (dédoublonne pré-chauffe + prepareCarousel).
   * @param {string} placement
   * @param {number} max
   * @returns {Promise<Array<{ product: Record<string, any>, cardEl: Element }>>}
   */
  #getNostoCards(placement, max) {
    const cached = this.#nostoCache.get(placement);
    if (cached) return cached;

    const promise = (async () => {
      try {
        const products = await requestNostoReco(placement);
        return await fetchNostoCards(products, this.constructor.SECTION_NAME, max);
      } catch (_) {
        return [];
      }
    })();

    this.#nostoCache.set(placement, promise);
    return promise;
  }

  /**
   * Pré-chauffe les recos des onglets Nosto (lit `data-nosto-placement` sur les panneaux,
   * toujours présents au SSR même non chargés) → 1er clic instantané depuis le cache.
   */
  #prewarmNostoTabs() {
    for (const panel of this.refs.panels ?? []) {
      const placement = panel.dataset.nostoPlacement;
      if (!placement) continue;
      const max = parseInt(panel.dataset.nostoProductsToShow ?? '', 10) || 8;
      const run = () => this.#getNostoCards(placement, max);
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(run, { timeout: 2500 });
      } else {
        setTimeout(run, 1500);
      }
    }
  }

  /**
   * Masque l'onglet correspondant à un carousel (via son panneau parent).
   * @param {HTMLElement} carousel
   */
  #hideCarouselTab(carousel) {
    const panels = this.refs.panels ?? [];
    const index = panels.findIndex((panel) => panel.contains(carousel));
    if (index >= 0) this.hideTab(index);
  }
}

customElements.define('ax-tabbed-product-lists-component', AxTabbedProductListsComponent);
